import { useEffect, useState } from 'react'
import { Save, Building2, Trash2 } from 'lucide-react'
import { Button } from '@brix/ui'
import { supabase } from '@/infrastructure/supabase/client'
import { useAuthStore } from '@/store/authStore'
import type { BillingProfile } from './InvoicePDF'

const EMPTY: BillingProfile = {}

export function BillingProfileForm() {
  const { tenant } = useAuthStore()
  const [form, setForm] = useState<BillingProfile>(EMPTY)
  const [loading, setLoading] = useState(true)
  const [hasProfile, setHasProfile] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [saved, setSaved] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  useEffect(() => {
    if (!tenant?.id) return
    supabase
      .from('billing_profiles')
      .select('*')
      .eq('tenant_id', tenant.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) { setForm(data as BillingProfile); setHasProfile(true) }
        setLoading(false)
      })
  }, [tenant?.id])

  const set = (field: keyof BillingProfile) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm((prev) => ({ ...prev, [field]: e.target.value || null }))

  const handleSave = async () => {
    if (!tenant?.id) return
    setSaving(true)
    await supabase.from('billing_profiles').upsert(
      { tenant_id: tenant.id, ...form },
      { onConflict: 'tenant_id' },
    )
    setHasProfile(true)
    setSaving(false)
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  const handleDelete = async () => {
    if (!tenant?.id) return
    setDeleting(true)
    await supabase.from('billing_profiles').delete().eq('tenant_id', tenant.id)
    setForm(EMPTY)
    setHasProfile(false)
    setDeleting(false)
    setConfirmDelete(false)
  }

  if (loading) return null

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-violet-600" />
          <h2 className="text-sm font-semibold text-gray-900">Billing details</h2>
          <span className="text-xs text-gray-400 font-normal">(optional)</span>
        </div>
        {hasProfile && !confirmDelete && (
          <button
            onClick={() => setConfirmDelete(true)}
            className="flex items-center gap-1.5 text-xs text-red-500 hover:text-red-700 transition-colors"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Remove details
          </button>
        )}
      </div>

      {confirmDelete && (
        <div className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 flex items-center justify-between">
          <p className="text-sm text-red-700">Remove all billing details? This cannot be undone.</p>
          <div className="flex items-center gap-2 ml-4 shrink-0">
            <button
              onClick={() => setConfirmDelete(false)}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={() => { void handleDelete() }}
              disabled={deleting}
              className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              {deleting ? 'Removing…' : 'Yes, remove'}
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Company / Legal name" value={form.company_name} onChange={set('company_name')} placeholder="Acme Inc." />
        <Field label="Tax ID / VAT number" value={form.tax_id} onChange={set('tax_id')} placeholder="US12-3456789" />
        <Field label="Address line 1" value={form.address_line1} onChange={set('address_line1')} placeholder="123 Main St" />
        <Field label="Address line 2" value={form.address_line2} onChange={set('address_line2')} placeholder="Suite 4B" />
        <Field label="City" value={form.city} onChange={set('city')} placeholder="San Francisco" />
        <Field label="State / Province" value={form.state} onChange={set('state')} placeholder="California" />
        <Field label="Postal code" value={form.postal_code} onChange={set('postal_code')} placeholder="94103" />
        <label htmlFor="billing-country" className="flex flex-col gap-1">
          <span className="text-xs font-medium text-gray-600">Country</span>
          <select
            id="billing-country"
            value={form.country ?? 'US'}
            onChange={set('country')}
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
          >
            <option value="US">United States</option>
            <option value="CA">Canada</option>
            <option value="GB">United Kingdom</option>
            <option value="AU">Australia</option>
            <option value="DE">Germany</option>
            <option value="FR">France</option>
            <option value="ES">Spain</option>
            <option value="MX">Mexico</option>
            <option value="AR">Argentina</option>
            <option value="CO">Colombia</option>
            <option value="CL">Chile</option>
            <option value="BR">Brazil</option>
          </select>
        </label>
        <div className="sm:col-span-2">
          <Field label="Billing email" value={form.billing_email} onChange={set('billing_email')} placeholder="billing@yourcompany.com" type="email" />
        </div>
      </div>

      <div className="mt-5 flex items-center gap-3">
        <Button onClick={() => { void handleSave() }} disabled={saving} className="flex items-center gap-2">
          <Save className="h-4 w-4" />
          {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save details'}
        </Button>
        <p className="text-xs text-gray-400">Included on your receipt and invoice PDFs.</p>
      </div>
    </div>
  )
}

function Field({
  label, value, onChange, placeholder, type = 'text',
}: {
  label: string
  value?: string | null
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  placeholder?: string
  type?: string
}) {
  const id = `billing-${label.replace(/\W+/g, '-').toLowerCase()}`
  return (
    <label htmlFor={id} className="flex flex-col gap-1">
      <span className="text-xs font-medium text-gray-600">{label}</span>
      <input
        id={id}
        type={type}
        aria-label={label}
        value={value ?? ''}
        onChange={onChange}
        placeholder={placeholder}
        className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
      />
    </label>
  )
}
